import React, { useEffect, useRef, useCallback, useMemo, useTransition } from 'react';
import {useWebViewStore,IWebView} from "./useWebViewStore";
import WebViewWrapper from './WebViewWrapper';
import { useChannelMessage } from '../channel-message/ChannelMessagingProvider';

const WebViewManager = () => {
    const { webviews } = useWebViewStore();
    const { initClient  } = useChannelMessage();
    const mountedInstancesRef = useRef<Map<string, HTMLElement>>(new Map());
    const [, startTransition] = useTransition();

    // Memoize active webview
    const activeWebView = useMemo(() => 
        webviews.find((webview: IWebView) => webview.active),
        [webviews]
    );

    const activeAppId = activeWebView?.id;
    const hasActiveApp = Boolean(activeWebView);

    // Track mounted instances - defer to prevent blocking navigation
    const handleWebViewMount = useCallback((id: string, url: string, element: HTMLIFrameElement) => {
        // Defer mounting updates to avoid blocking navigation clicks
        startTransition(() => {
            mountedInstancesRef.current.set(id, element);
        });
        console.log(`WebView ${id} mounted`);
    }, [startTransition]);

    const onWebViewReady = useCallback((id: string, url: string, element: HTMLIFrameElement) => {
        // Defer client initialization to prevent blocking UI
        console.log(`webview URL - `, url);
        startTransition(() => {
            initClient(element, id, url);
        });
    }, [initClient, startTransition]);

    // Cleanup removed webviews - defer to prevent blocking navigation
    useEffect(() => {
        const currentWebViewIds = new Set(webviews.map((wv: IWebView) => wv.id));
        console.log(`webviews - `, webviews);
        
        // Use setTimeout to defer cleanup and allow navigation to proceed
        const cleanupTimer = setTimeout(() => {
            startTransition(() => {
                for (const [id, element] of mountedInstancesRef.current.entries() as any) {
                    if (!currentWebViewIds.has(id)) {
                        console.log(`Cleaning up WebView ${id}`);
                        element.remove();
                        mountedInstancesRef.current.delete(id);
                    }
                }
            });
        }, 0);
        
        return () => clearTimeout(cleanupTimer);
    }, [webviews, startTransition]);

    return (
        <div 
            id="app-stack"
            style={{ 
                display: hasActiveApp ? 'block' : 'none',
                position: 'relative',
                width: '100%',
                height: '100%',
                overflow: 'hidden',
                backgroundColor: '#fff'
            }}
        >
            {webviews.map((webview: IWebView) => (
                <WebViewWrapper
                    key={webview.id}
                    webview={webview}
                    isActive={webview.id === activeAppId}
                    onMount={handleWebViewMount}
                    onReady={onWebViewReady}
                />
            ))}
        </div>
    );
};

export default React.memo(WebViewManager);